import { addDays, today } from "./dates.js";
export const WORK_LOG_STATUSES = ["PENDING", "APPROVED", "REJECTED"];
export function migrateWorkLogs(s) {
  if (Array.isArray(s.workLogs)) return s;
  s.workLogs = [];
  for (const [key, a] of Object.entries(s.att || {})) {
    const [depId] = key.split("|"),
      d = s.deployments.find((d) => d.id === depId);
    if (!d) continue;
    let quantity = 0,
      ot = 0,
      night = 0;
    a.dates.forEach((date, i) => {
      const last = i === a.dates.length - 1,
        part = (n) => Math.round(((n || 0) / a.dates.length) * 1000) / 1000,
        units = last
          ? Math.round(((a.units || 0) - quantity) * 1000) / 1000
          : part(a.units),
        o = last ? Math.round(((a.ot || 0) - ot) * 1000) / 1000 : part(a.ot),
        nd = last
          ? Math.round(((a.nd || 0) - night) * 1000) / 1000
          : part(a.nd);
      quantity += units;
      ot += o;
      night += nd;
      s.workLogs.push({
        id: `WL-${depId}-${date}`,
        deployment: d.id,
        emp: d.emp,
        project: d.project,
        date,
        hours: 8,
        units,
        ot: o,
        night: nd,
        unit: d.unit || "day",
        payRate: d.rate,
        billRate: d.bill,
        allowance: d.perDiem || 0,
        type: d.type,
        status: "APPROVED",
        by: "system",
        approvedBy: "system",
        approvedAt: new Date().toISOString(),
        note: "Imported from existing approved time records",
        legacy: true,
      });
    });
  }
  return s;
}
export function approvedAttendance(s, d, from, to) {
  const logs = (s.workLogs || []).filter(
    (l) =>
      l.deployment === d.id &&
      l.status === "APPROVED" &&
      l.date >= from &&
      l.date <= to,
  );
  return {
    dates: [...new Set(logs.map((l) => l.date))],
    units: logs.reduce((a, l) => a + l.units, 0),
    ot: logs.reduce((a, l) => a + l.ot, 0),
    nd: logs.reduce((a, l) => a + l.night, 0),
    logs,
  };
}
export function validateWorkLog(s, input) {
  const d = s.deployments.find((x) => x.id === input.deployment);
  if (!d) throw new Error("Choose a personnel deployment.");
  const date = input.date;
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    Number.isNaN(Date.parse(date + "T00:00:00Z")) ||
    date < d.from ||
    date > (d.endedOn || d.to || "9999-12-31") ||
    date > today()
  )
    throw new Error(
      "The work date must be within the deployment and cannot be in the future.",
    );
  if (
    s.runs.some((r) => r.status !== "DRAFT" && date >= r.from && date <= r.to)
  )
    throw new Error("This date is locked by an approved payroll run.");
  const values = Object.fromEntries(
    ["hours", "units", "ot", "night", "nightOT"].map((k) => [
      k,
      Number(input[k] || 0),
    ]),
  );
  for (const [key, n] of Object.entries(values))
    if (
      !Number.isFinite(n) ||
      n < 0 ||
      (Math.round(n * 1000) !== n * 1000 &&
        Math.abs(Math.round(n * 1000) - n * 1000) > 0.0001)
    )
      throw new Error(
        `Enter a valid ${key} value with up to three decimal places.`,
      );
  if (
    values.hours <= 0 ||
    values.hours > 8 ||
    values.ot > 16 ||
    values.night > values.hours + values.ot ||
    values.nightOT > values.night ||
    values.nightOT > values.ot ||
    values.hours + values.ot > 24
  )
    throw new Error(
      "Enter 0–8 regular hours, valid overtime, and night hours within total hours.",
    );
  if (d.type === "DAILY") values.units = 0;
  const same = (s.workLogs || []).filter(
    (l) =>
      l.id !== input.id &&
      l.emp === d.emp &&
      l.date === date &&
      l.status !== "REJECTED",
  );
  if (same.some((l) => l.deployment === d.id))
    throw new Error(
      "This deployment already has a log for that date. Reject the incorrect log before recording a replacement.",
    );
  if (
    same.reduce((a, l) => a + l.hours, 0) + values.hours > 8.0001 ||
    same.reduce((a, l) => a + l.hours + l.ot, 0) + values.hours + values.ot >
      24.0001
  )
    throw new Error(
      "This worker’s hours across all projects exceed the daily limit.",
    );
  return {
    ...values,
    deployment: d.id,
    emp: d.emp,
    project: d.project,
    date,
    unit: d.unit || "day",
    payRate: d.rate,
    billRate: d.bill,
    allowance: d.perDiem || 0,
    type: d.type,
    note: String(input.note || "")
      .trim()
      .slice(0, 1000),
  };
}
export function createWorkLog(s, input, user, id = crypto.randomUUID()) {
  const values = validateWorkLog(s, input);
  const log = {
    id: "WL-" + id,
    ...values,
    status: "PENDING",
    by: user,
    createdAt: new Date().toISOString(),
  };
  (s.workLogs ||= []).push(log);
  return log;
}
export function approveWorkLog(s, id, user, decision = "APPROVED") {
  const log = s.workLogs.find((l) => l.id === id);
  if (!log || log.status !== "PENDING")
    throw new Error("Only pending work logs can be reviewed.");
  if (log.by === user)
    throw new Error("A different person must review the work log.");
  if (!["APPROVED", "REJECTED"].includes(decision))
    throw new Error("Invalid review decision.");
  validateWorkLog(s, log);
  log.status = decision;
  log.approvedBy = user;
  log.approvedAt = new Date().toISOString();
  return log;
}
